import { FC, useEffect, useState, ChangeEvent } from "react"
import {
  Avatar,
  Button,
  Alert,
  Dialog,
  DialogActions,
  DialogTitle,
} from "@mui/material"

import { useUploadAvatarToServer } from "../../../hooks/useUploadAvatarToServer"
import { useDeleteUser } from "../hooks/useDeleteUser"
import { useProfileSettingsEditor } from "../hooks/useProfileSettings"

import { selectUserId } from "../../../app/data/appTokensStore"
import { useAppSelector } from "../../../app/hooks"

import AvatarFallback from "../../../assets/svg/menu-icons-pack/avatar.svg?react"
import ClearInputIcon from "../../../assets/svg/profile-settings-view/delete-round-icon.svg?react"
import PhotoIcon from "../../../assets/svg//profile-settings-view/add-avatar.svg?react"
import ChangeIcon from "../../../assets/svg//profile-settings-view/change-avatar.svg?react"
import DeleteAvatar from "../../../assets/svg//profile-settings-view/delete-avatar.svg?react"
import LocationSpinner from "../../../common/ui-kit/spinner"

import {
  avatarUploadBtn,
  userAvatar,
  profileViewButton,
} from "./profileAndViewStyle"
import s from "./styles.module.scss"

const ProfileAndViewSettings: FC = () => {
  const [profileInfo, setProfileInfo] = useState<{
    username: string
    firstName: string
    lastName: string
    birthday?: string
    avatar?: string
  }>({
    username: "",
    firstName: "",
    lastName: "",
    birthday: "",
    avatar: "",
  })

  const userId = useAppSelector(selectUserId)

  const { updateProfile, isLoading, error, currentUser } =
    useProfileSettingsEditor()
  const { upload, resultUrl } = useUploadAvatarToServer(
    userId ? userId.toString() : "",
  )
  const { deleteUser } = useDeleteUser()

  const [preview, setPreview] = useState<string>("")
  const [stateResultUrl, setStateResultUrl] = useState<string>("")
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    console.log(currentUser)
    if (currentUser) {
      setProfileInfo((prev) => ({
        ...prev,
        username: currentUser.username ?? "",
        firstName: currentUser.first_name ?? "",
        lastName: currentUser.last_name ?? "",
        birthday: currentUser.birthday ?? "",
        avatar: currentUser.avatar_url ?? "",
      }))
    }
  }, [currentUser])

  useEffect(() => {
    if (resultUrl) {
      setStateResultUrl(resultUrl)
    }
  }, [resultUrl])

  const handleAvatarChange = async (
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile && userId) {
      try {
        const fileBuffer = await selectedFile.arrayBuffer()
        upload(fileBuffer)
        const previewUrl = URL.createObjectURL(selectedFile)
        setPreview(previewUrl)
      } catch (error) {
        console.error("Error uploading file:", error)
      }
    }
  }
  const handleAvatarRemove = (): void => {
    const { username } = profileInfo

    setPreview("")
    setProfileInfo((prev) => ({
      ...prev,
      avatar: "",
    }))
    updateProfile({
      username,
      user_avatar: "",
    })
    setStateResultUrl("")
  }

  const handleSubmit = (): void => {
    const { username, firstName, lastName, birthday } = profileInfo

    updateProfile({
      username,
      first_name: firstName,
      last_name: lastName,
      birthday,
      ...(stateResultUrl && { user_avatar: stateResultUrl }),
    })
    setStateResultUrl("")
  }

  const handleDelete = () => {
    setDialogOpen(false)
    deleteUser()
  }

  const capitalize = (str: string): string =>
    str
      .trim()
      .toLowerCase()
      .split(" ")
      .map((word) => word[0].toUpperCase() + word.slice(1))
      .join(" ")
  const today = new Date()
  const minAge = new Date(today.setFullYear(today.getFullYear() - 12))
    .toISOString()
    .slice(0, 10) // min age 12 years

  const getCurrentDate = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")

    return `${year}-${month}-${day}`
  }

  const placeholderText = () => {
    if (!profileInfo.birthday) {
      return "Дата рождения"
    }
    if (profileInfo.birthday === "0001-01-01") {
      return "Дата рождения"
    }
    return new Date(profileInfo.birthday).toLocaleDateString()
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: "auto",
        }}
      >
        <LocationSpinner />
      </div>
    )
  }
  if (error) {
    return <div>{error.error}</div>
  }

  return (
    <div className={s.user}>
      <div className={s.user__info}>
        <div className={s.user__avatar}>
          <Avatar src={preview || profileInfo.avatar} sx={userAvatar}>
            <AvatarFallback className={s.user__avatar_fallback} />
          </Avatar>
          <Button
            sx={avatarUploadBtn}
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={preview ? <ChangeIcon /> : <PhotoIcon />}
          >
            <input
              type="file"
              accept="image/*, .png, .jpg"
              aria-label="add avatar"
              multiple={false}
              className={s.user__avatar_hidden_input}
              onChange={handleAvatarChange}
            />
          </Button>
          {(preview || profileInfo.avatar) && (
            <button
              className={s.user__avatar_delete}
              tabIndex={-1}
              aria-label="Удалить аватар"
              onClick={handleAvatarRemove}
            >
              <DeleteAvatar />
            </button>
          )}
        </div>
        {currentUser?.first_name || currentUser?.last_name ? (
          <div className={s.user__fullname}>
            {currentUser &&
              capitalize(
                `${currentUser.first_name || ""} ${
                  currentUser.last_name || ""
                }`,
              )}
          </div>
        ) : null}
        <div className={s.user__username}>{`@${currentUser?.username}`}</div>
      </div>
      <form
        name="profileSettingsForm"
        className={s.form__container}
        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        <div className={s.form__items}>
          <div className={s.form__item}>
            <input
              className={s.form__input}
              type="text"
              name="first_name"
              placeholder="Имя"
              maxLength={50}
              spellCheck="false"
              autoComplete="off"
              autoCapitalize="words"
              style={{
                textTransform: "capitalize",
              }}
              value={profileInfo.firstName}
              onChange={(e) =>
                setProfileInfo((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
            />
            {profileInfo.firstName && (
              <button
                className={s.form__input_clear}
                type="button"
                onClick={() =>
                  setProfileInfo((prev) => ({
                    ...prev,
                    firstName: "",
                  }))
                }
              >
                <ClearInputIcon />
              </button>
            )}
          </div>
          <div className={s.form__item}>
            <input
              type="text"
              className={s.form__input}
              name="last_name"
              placeholder="Фамилия"
              maxLength={50}
              spellCheck="false"
              autoComplete="off"
              autoCapitalize="words"
              style={{
                textTransform: "capitalize",
              }}
              value={profileInfo.lastName}
              onChange={(e) =>
                setProfileInfo((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
            />
            {profileInfo.lastName && (
              <button
                className={s.form__input_clear}
                type="button"
                onClick={() =>
                  setProfileInfo((prev) => ({
                    ...prev,
                    lastName: "",
                  }))
                }
              >
                <ClearInputIcon />
              </button>
            )}
          </div>
          <div className={s.form__item}>
            <input
              type="text"
              placeholder="Логин *"
              name="username"
              className={s.form__input}
              spellCheck="false"
              autoComplete="off"
              autoCapitalize="off"
              maxLength={50}
              required
              value={profileInfo.username}
              onChange={(e) =>
                setProfileInfo((prev) => ({
                  ...prev,
                  username: e.target.value,
                }))
              }
            />
            {profileInfo.username && (
              <button
                className={s.form__input_clear}
                type="button"
                onClick={() =>
                  setProfileInfo((prev) => ({
                    ...prev,
                    username: "",
                  }))
                }
              >
                <ClearInputIcon />
              </button>
            )}
          </div>
          {!profileInfo.username && (
            <Alert severity="error">Это поле не может быть пустым</Alert>
          )}
          <div className={s.form__item}>
            <input
              type="date"
              className={s.form__input}
              name="birthday"
              placeholder={placeholderText()}
              max={getCurrentDate()}
              style={{
                color:
                  profileInfo.birthday === "" ||
                  profileInfo.birthday === "0001-01-01"
                    ? "var(--input-placeholder)"
                    : undefined,
              }}
              value={
                profileInfo.birthday == "0001-01-01"
                  ? getCurrentDate()
                  : profileInfo.birthday
              }
              onChange={(e) => {
                setProfileInfo((prev) => ({
                  ...prev,
                  birthday:
                    e.target.value == "" ? "0001-01-01" : e.target.value,
                }))
              }}
            />
          </div>
          {profileInfo.birthday && profileInfo.birthday > minAge ? (
            <Alert severity="error">"Вы должны быть старше 12 лет"</Alert>
          ) : null}
        </div>
        <Button
          variant="text"
          type="submit"
          disabled={!profileInfo.username}
          aria-label="save changes"
          sx={profileViewButton}
          style={{
            ...((!profileInfo.username ||
              (profileInfo.birthday && profileInfo.birthday > minAge)) && {
              background: "var(--btns-disabled-bg)",
              color: "var(--btns-disabled-text)",
              pointerEvents: "none",
            }),
          }}
        >
          Применить
        </Button>
      </form>
      <Button
        variant="text"
        className={s.user__btn_delete}
        sx={profileViewButton}
        aria-label="delete account"
        onClick={() => setDialogOpen(true)}
      >
        Удалить аккаунт
      </Button>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle
          id="alert-dialog-title"
          style={{
            color: "var(--Schemes-On-Surface-Variant)",
            fontSize: "16px",
          }}
        >
          {"Удалить аккаунт?"}
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={profileViewButton}>
            Отмена
          </Button>
          <Button onClick={handleDelete} sx={profileViewButton} autoFocus>
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
export default ProfileAndViewSettings
